import React, { FC } from 'react'
import { motion } from 'framer-motion'
import { Button, LinkButton } from '@/atoms/Button'
import { FlexRow } from '@/atoms/FlexContainers'
import { TicketIcon } from '@/atoms/Icons/TicketIcon'
import { Image } from '@/atoms/Image'
import { CaptionSM, Text, TitleXXS } from '@/atoms/Text'
import { useLoginUrl } from '@/utils/auth-utils'
import { Translate, useTranslate } from '@/utils/translate/translate-client'
import useWindowSize from '@/utils/useWindowSize'
import { CurrentTheatricalRelease } from '@/views/TicketsLandingView/types'

const variants = {
  hidden: {
    x: '50%',
    y: '-150%',
    opacity: 0,
  },
  visible: {
    x: '50%',
    y: 0,
    opacity: 1,
    transition: { type: 'spring', stiffness: 300, damping: 30 },
  },
  desktopVisible: {
    x: 0,
    y: 0,
    opacity: 1,
    transition: { type: 'spring', stiffness: 300, damping: 30 },
  },
  desktopHidden: {
    x: '100%',
    y: 0,
    opacity: 0,
  },
}

interface Props {
  isOpen: boolean
  onClose: () => void
  heroRelease: CurrentTheatricalRelease
}
export const LoginPromptModal: FC<Props> = ({ isOpen, onClose, heroRelease }) => {
  const { t } = useTranslate('tickets')
  const { innerWidth } = useWindowSize()
  const { loginUrl, trackLoginStarted } = useLoginUrl()
  const isMobileWidth = innerWidth < 768

  return (
    <motion.div
      initial="hidden"
      animate={isOpen ? 'visible' : 'hidden'}
      exit="hidden"
      variants={{
        hidden: isMobileWidth ? variants.hidden : variants.desktopHidden,
        visible: isMobileWidth ? variants.visible : variants.desktopVisible,
      }}
      style={{ boxShadow: '0px 20px 28px -8px #000000A3' }}
      className="absolute right-1/2 top-4 z-[100] flex w-full max-w-[361px] items-stretch overflow-hidden rounded-2xl bg-white md:right-4 md:max-w-[400px] 3xs:top-0 3xs:max-w-full 3xs:rounded-none"
    >
      <div className="relative w-[88px] shrink-0 grow-1 bg-black md:w-[100px] 3xs:hidden">
        <Image priority src={heroRelease.angelKeyArt2x3Image} fill alt={heroRelease.title} className="object-cover" />
      </div>
      <div className="flex flex-col p-4">
        <FlexRow className="hidden items-center gap-1 pt-2 md:flex">
          <TicketIcon size={20} color={'black'} />
          <Text className="md:photon-title-xs  " weight="semibold">
            {t('didYouAlreadyBuyTickets', 'Did you already buy tickets?')}
          </Text>
        </FlexRow>
        <FlexRow className="items-center gap-1 md:hidden">
          <TicketIcon size={18} color={'black'} />
          <TitleXXS weight="semibold">{t('didYouAlreadyBuyTickets', 'Did you already buy tickets?')}</TitleXXS>
        </FlexRow>
        <Text className="photon-caption-xs md:photon-caption-sm mt-1 md:mt-2" weight="medium" color="core-gray-700">
          {t('logInToYourAccountHere', 'Log in to your account to view your tickets right here.')}
        </Text>
        <FlexRow className="items-center gap-1 pt-1 md:pt-0">
          <LinkButton
            href={loginUrl}
            className="mr-auto mt-3 flex-1 !py-2  px-8 !text-xs md:mt-4"
            onClick={() => trackLoginStarted('top-nav')}
            variant={'black'}
          >
            <CaptionSM className="whitespace-nowrap">
              <Translate t={t} i18nKey="logIn">
                Log In
              </Translate>
            </CaptionSM>
          </LinkButton>
          <Button onClick={onClose} className="mr-auto mt-3 !py-2 px-8 !text-xs md:mt-4" variant="gray">
            {t('close', 'Close')}
          </Button>
        </FlexRow>
      </div>
    </motion.div>
  )
}
